import type { ReactNode } from 'react';
import { MoveCard } from '@/components/molecules';
import { findMoveByIdAnyScope } from '@/data/moves';
import type { Character, MoveRef } from '@/schemas/character';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { useExpandedState } from '@/hooks/useExpandedState';
import { isMoveSetupComplete } from '@/features/character-builder/services/moveSetupComplete';
import { SheetSection } from './SheetSection';

// organism — DWR FullSheet mosse expandable. Pattern .move-card mockup
// riusato dal builder (PR 6+8). Tap chevron → espande description + setup
// compilato (selectedChoices / selectedChoiceGroups / narrativeFieldValues
// / recruitOutcome) inline dentro la card.
//
// **PR feature/hub-mobile-complete (FI-48 + FI-59)**: estratto da FullSheet
// per max-lines + chiusura FI-59 (setup DENTRO MoveCard via slot setupSlot).

interface FullSheetMovesProps {
  character: Character;
}

export function FullSheetMoves({ character }: FullSheetMovesProps) {
  const { expanded, toggleExpanded } = useExpandedState(character.moves.map((m) => m.id));

  if (character.moves.length === 0) {
    return (
      <SheetSection title="Mosse">
        <p className="text-ink-soft italic">Nessuna mossa.</p>
      </SheetSection>
    );
  }
  return (
    <SheetSection title="Mosse">
      <div className="flex flex-col gap-2">
        {character.moves.map((moveRef) => {
          const move = findMoveByIdAnyScope(character.classId, moveRef.id);
          const setupSlot = renderMoveSetup(moveRef, move);
          const isComplete = move
            ? isMoveSetupComplete(move, moveRef, character.archetypeId)
            : true;
          return (
            <MoveCard
              key={moveRef.id}
              move={move}
              fallbackId={moveRef.id}
              expanded={expanded.has(moveRef.id)}
              onToggle={() => toggleExpanded(moveRef.id)}
              setupComplete={move && hasMoveSetup(move) ? isComplete : false}
              trailing={<MoveTypeBadge move={move} />}
              setupSlot={setupSlot}
            />
          );
        })}
      </div>
    </SheetSection>
  );
}

function MoveTypeBadge({ move }: { move: Move | undefined }) {
  if (!move) return null;
  const note = moveTypeNote(move.type);
  if (!note) return null;
  return (
    <span className="text-ink-faint text-[0.7rem] font-normal tracking-normal normal-case italic">
      · {note}
    </span>
  );
}

function moveTypeNote(type: string): string {
  if (type === 'starting') return 'iniziale';
  if (type === 'advanced_2_5') return 'avanzata L2-5';
  if (type === 'advanced_6_10') return 'avanzata L6-10';
  if (type === 'class_special') return 'speciale di classe';
  if (type === 'common') return 'comune';
  if (type === 'base') return 'base';
  if (type === 'system') return 'sistema';
  return type;
}

/**
 * Renderizza il setup compilato della mossa (sotto la description) — solo
 * se presente. Pattern FI-18 / FI-21 / D5 / D8a: 4 famiglie di setup, ognuna
 * con helper dedicato per restare entro complexity 10.
 */
function renderMoveSetup(moveRef: MoveRef, move: Move | undefined): ReactNode {
  if (!move) return null;
  const rows: ReactNode[] = [
    ...renderSelectedChoicesRows(moveRef, move),
    ...renderChoiceGroupsRows(moveRef, move),
    ...renderNarrativeFieldsRows(moveRef, move),
    ...renderRecruitOutcomeRow(moveRef),
  ];
  if (rows.length === 0) return null;
  return <dl className="m-0 flex flex-col gap-1 p-0 text-[0.9rem]">{rows}</dl>;
}

function renderSelectedChoicesRows(moveRef: MoveRef, move: Move): ReactNode[] {
  if (moveRef.selectedChoices.length === 0 || !move.choices) return [];
  const choices = move.choices;
  const labels = moveRef.selectedChoices.map((id) => choices.find((o) => o.id === id)?.label ?? id);
  return [
    <SetupRow key="choices" label="Scelta">
      {labels.join(', ')}
    </SetupRow>,
  ];
}

function renderChoiceGroupsRows(moveRef: MoveRef, move: Move): ReactNode[] {
  if (!moveRef.selectedChoiceGroups || !move.choiceGroups) return [];
  const selectedGroups = moveRef.selectedChoiceGroups;
  return move.choiceGroups.flatMap((group) => {
    const selected = selectedGroups[group.id];
    if (!selected || selected.length === 0) return [];
    const labels = selected.map(
      (optId) => group.options.find((o) => o.id === optId)?.label ?? optId,
    );
    return [
      <SetupRow key={`group-${group.id}`} label={group.label}>
        {labels.join(', ')}
      </SetupRow>,
    ];
  });
}

function renderNarrativeFieldsRows(moveRef: MoveRef, move: Move): ReactNode[] {
  if (!moveRef.narrativeFieldValues || !move.narrativeFields) return [];
  const values = moveRef.narrativeFieldValues;
  return move.narrativeFields.flatMap((field) => {
    const value = values[field.id];
    if (!value || value.trim().length === 0) return [];
    return [
      <SetupRow key={`field-${field.id}`} label={field.label}>
        {value}
      </SetupRow>,
    ];
  });
}

function renderRecruitOutcomeRow(moveRef: MoveRef): ReactNode[] {
  if (!moveRef.recruitOutcome) return [];
  return [
    <SetupRow key="recruit" label="Reclutamento">
      {formatRecruitOutcome(moveRef.recruitOutcome)}
    </SetupRow>,
  ];
}

function SetupRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="font-display text-ink-soft text-[0.72rem] tracking-[0.08em] uppercase">
        {label}:
      </dt>
      <dd className="text-ink m-0 flex-1 text-[0.9rem]">{children}</dd>
    </div>
  );
}

function formatRecruitOutcome(outcome: string): string {
  if (outcome === '10+') return 'Successo (10+)';
  if (outcome === '7-9') return 'Esito misto (7-9)';
  return outcome;
}
